import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {Accordion, Alert, Button, Col, Form, Row} from "react-bootstrap";
import { useSubheader } from "@metronic/layout";
import { FamilyData } from "@components/panels/family-data/family-data";
import { HospitalData } from "@components/panels/hospital-data/hospital-data";
import { InssuredData } from "@components/panels/insured-data/inssured-data";
import { SufferingData } from "@components/panels/suffering-data/suffering-data";
import { Documentation } from "@components/panels/documentation/documentation";
import { Binnacle } from "@components/panels/binnacle/binnacle";
import { MedicAssigned } from "@components/panels/medic-assigned/medic-assigned";
import { update, getBySlug } from "@services/hospitalization";
import {useHistory, useLocation} from "react-router-dom";
import axios from "axios";
import { isEmpty } from "@utils/check-object-empty";
import { toBooleanOrNull } from "@utils/generic-functions";
import moment from "moment";
import "moment-timezone";
import BlockCaseContext from "@contexts/block-case-context";
import { useCommentValidation } from "@contexts/comment-validation-context";
import WithCommentValidation from "@components/shared/with-comment-validation";
import {CASE_STATUS} from "@constants/case-statuses";
import {INCOME_TYPE} from "@constants/income-types";
import AuthLetters from "@components/panels/auth-letter/auth-letters";
import { useAutomaticCommentSave } from "@hooks/use-automatic-comment-save";
import { useMultiFormManager } from "@contexts/multi-form-manager-context";
import WithMultiFormManager from "@components/shared/with-multi-form-manager";
import { useHospitalizationQueryParams } from "@hooks/use-query-params";
import { flowRight as compose } from "lodash";
import {getMexicoTimestamp} from "@utils/date-utils";
import swal from "sweetalert";
import { getCaseDetails, updateCaseDetails } from "@services/case-details";
import { getCaseDocumentAnalyses } from "@services/document-analysis";
import { useDocumentIntake, linkAnalysesToCase } from "@components/document-intake/use-document-intake";
import IntakeSummary, { IntakeHaloStyles } from "@components/document-intake/intake-summary";
import { useIntakeHalos } from "@components/document-intake/use-intake-halos";
import {
  buildPrefill,
  getDocumentChecklist,
  getIntakeNotices,
  getMissingDocuments,
  getTutorName,
  isMinorByBirthdate,
  getHaloClass,
  DOCUMENT_LABELS,
} from "@components/document-intake/document-intake-prefill";
import { getRequiredDocuments } from "@components/document-intake/document-intake-profiles";

// Campos que se necesitan para abrir el caso; los paneles los validan, aqui se vigilan para el
// inventario de "datos listos / faltantes" de la captura asistida.
const INTAKE_TRACKED_FIELDS = [
  { name: "hospitalId", label: "Hospital" },
  { name: "reporter", label: "Quien reporta" },
  { name: "hospitalEmail", label: "Correo del hospital" },
  { name: "hospitalPhone", label: "Teléfono del hospital" },
  { name: "assuredName", label: "Nombre del asegurado" },
  { name: "assuredFlastname", label: "Apellido paterno" },
  { name: "assuredEmail", label: "Correo del asegurado" },
  { name: "policy", label: "Póliza" },
  { name: "incomeDatetime", label: "Fecha de ingreso" },
  { name: "hospitalIden", label: "Folio del hospital" },
  { name: "diagnosis", label: "Diagnóstico" },
  { name: "familiarName", label: "Nombre del familiar" },
  { name: "familiarMobile", label: "Celular del familiar" },
  { name: "treatmentDoctorName", label: "Médico tratante" },
  // Solo se lee, para saber si el paciente es menor de edad.
  { name: "assuredBrthdate", silent: true },
];

const Hospitalization = (props) => {
  const [shouldBlockCase, setShouldBlockCase] = useState(true);
  const [caseRecord, setCaseRecord] = useState({});
  const [caseDetails, setCaseDetails] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [tutorName, setTutorName] = useState("");
  const formsRef = useRef(null);
  const sendRef = useRef(null);
  const { requestDocumentList, intakeModal } = useDocumentIntake();

  const history = useHistory();
  const queryParams = useHospitalizationQueryParams();
  const subheader = useSubheader();
  
  const { validateCommentRequired } = useCommentValidation();
  const { validateAllForms, getAllFormValues, getFormCount, getRegisteredFormNames } = useMultiFormManager();
  const { saveCommentAutomatically } = useAutomaticCommentSave({ caseData: caseRecord });
  const { saveCommentAutomaticallyUnam } = useAutomaticCommentSave({
    caseData: caseRecord,
    eventStage: null,
    comment: "FAVOR DE FACTURAR A UNAM, DE NO HACERLO NO SE PODRÁ PAGAR",
    isPageable: false,
    hideReason: true,
  });

  const { slug } = props.match.params;

  const getCaseBySlug = useCallback(() =>
    getBySlug(slug).then((response) => setCaseRecord(response.data)), [slug]
  );

  useEffect(() => {
    subheader.setTitle("Ingreso hospitalario");
    getCaseBySlug();
  }, [subheader, getCaseBySlug]);

  const refreshAnalyses = useCallback(
    () => getCaseDocumentAnalyses(slug).then((response) => {
      setAnalyses(response.data);
      return response.data;
    }),
    [slug]
  );

  useEffect(() => {
    if (!caseRecord.id) {
      return;
    }
    getCaseDetails(caseRecord.id).then((response) => setCaseDetails(response.data)).catch(() => {});
    refreshAnalyses().catch(() => {});
  }, [caseRecord.id, refreshAnalyses]);

  // Solo los casos abiertos con apoyo de IA llevan el asistente y la validacion de documentos.
  const aiAssisted = caseDetails?.automaticCreation === true;

  const isHospitalReport = useCallback(
    (incomeType = caseRecord.incomeType) =>
      incomeType
        ? incomeType === INCOME_TYPE.HOSPITAL_REPORT.value
        : queryParams.isScheduledSurgery === null && queryParams.isNewCase === null,
    [caseRecord.incomeType, queryParams]
  );

  const aiFields = useMemo(() => new Set(Object.keys(buildPrefill(analyses).sources)), [analyses]);
  const { items: intakeItems, values: watchedValues } = useIntakeHalos({
    containerRef: formsRef,
    fields: INTAKE_TRACKED_FIELDS,
    aiFields,
    enabled: aiAssisted,
  });

  // Menor de edad: se toma la fecha capturada en el formulario o, si no, la del caso.
  const birthdate = watchedValues.assuredBrthdate || caseRecord.assuredBrthdate || "";
  const minor = aiAssisted && isMinorByBirthdate(birthdate);
  const intakeNotices = useMemo(() => getIntakeNotices(analyses, { birthdate }), [analyses, birthdate]);
  const requiredDocuments = getRequiredDocuments(isHospitalReport(), { minor });
  const documentChecklist = getDocumentChecklist(requiredDocuments, analyses);

  // El nombre del padre o tutor arranca con lo guardado o lo que leyo la IA de su identificacion.
  useEffect(() => {
    setTutorName((current) => current || caseDetails?.tutorName || getTutorName(analyses));
  }, [caseDetails, analyses]);

  const combineFormDataWithTimestamps = useCallback((requestedStatusId, forceControlDesk = false) => {
    // Con documentacion incompleta el caso va a Mesa de Control aunque fuera cirugia programada.
    const caseStatusId = forceControlDesk ? CASE_STATUS.CONTROL_DESK : requestedStatusId;
    const allFormValues = getAllFormValues();
    
    const combinedData = {
      ...caseRecord,
      ...allFormValues.hospitalData || {},
      ...allFormValues.inssuredData || {}, 
      ...allFormValues.familyData || {},
      ...allFormValues.medicAssigned || {},
    };

    if (combinedData['vip'] === "Si") {
      combinedData['axaCategories'] = document.getElementById('axaCategories')?.value || '';
    }

    const mexicoTimestamp = getMexicoTimestamp();

    if (caseStatusId === CASE_STATUS.CONTROL_DESK) {
      combinedData["feamesaControl"] = mexicoTimestamp;
    } else if (caseStatusId === CASE_STATUS.MEDIC_INFORMATION) {
      combinedData["feaimedica"] = mexicoTimestamp;
    }

    if (!forceControlDesk && combinedData?.incomeType === INCOME_TYPE.SCHEDULED_SURGERY.value) {
      combinedData["caseStatusId"] = CASE_STATUS.MEDIC_INFORMATION;
      combinedData["feaimedica"] = mexicoTimestamp;
    } else {
      combinedData["caseStatusId"] = caseStatusId;
    }

    return combinedData;
  }, [caseRecord, getAllFormValues]);

  /**
   * Revisa que esten los documentos del expediente. Devuelve "complete", "incomplete" (la
   * persona decidio enviar sin ellos: va a Mesa de Control), "upload" (abrio la carga de los que
   * faltan y el envio se reintenta al terminar) o "cancel".
   */
  const resolveDocumentation = useCallback(async (caseStatusId, incomeType) => {
    const latest = await refreshAnalyses().catch(() => analyses);
    const missing = getMissingDocuments(getRequiredDocuments(isHospitalReport(incomeType), { minor }), latest);
    if (missing.length === 0) {
      return "complete";
    }

    const choice = await swal({
      title: "Falta documentación",
      text: `Se requiere documentación completa para aperturar el expediente.\n\nFaltan: ${missing
        .map((document) => document.label)
        .join(", ")}`,
      icon: "warning",
      buttons: {
        cancel: "Cancelar",
        send: { text: "Enviar a Mesa de Control", value: "send" },
        upload: { text: "Subir documentos", value: "upload" },
      },
    });

    if (choice === "send") {
      return "incomplete";
    }
    if (choice === "upload") {
      requestDocumentList(
        missing.flatMap((document) =>
          document.types.map((type) => ({ type, label: DOCUMENT_LABELS[type], required: false }))
        ),
        async (uploaded) => {
          await linkAnalysesToCase(slug, uploaded);
          await refreshAnalyses().catch(() => {});
          sendRef.current(caseStatusId);
        },
        "Documentos faltantes del expediente"
      );
      return "upload";
    }
    return "cancel";
  }, [analyses, isHospitalReport, minor, refreshAnalyses, requestDocumentList, slug]);

  const onClickUpdateCase = useCallback(async (caseStatusId = CASE_STATUS.CONTROL_DESK) => {
    const validation = await validateAllForms();
    
    if (!validation.allValid || !validateCommentRequired()) {
      return;
    }

    let documentation = "complete";
    if (aiAssisted) {
      documentation = await resolveDocumentation(caseStatusId, getAllFormValues().hospitalData?.incomeType);
      if (documentation === "upload" || documentation === "cancel") {
        return;
      }
    }
    const forceControlDesk = documentation === "incomplete";

    await saveCommentAutomatically();
    await saveCommentAutomaticallyUnam();
    const caseData = combineFormDataWithTimestamps(caseStatusId, forceControlDesk);
    await update(caseData, caseRecord.id);

    if (aiAssisted) {
      // Queda marcado como se abrio el caso: sin Mesa de Control, o por documentacion incompleta.
      await updateCaseDetails(caseRecord.id, {
        documentationIncomplete: forceControlDesk,
        controlDeskBypassed: caseData.caseStatusId === CASE_STATUS.MEDIC_INFORMATION,
        ...(minor && tutorName.trim() ? { tutorName: tutorName.trim() } : {}),
      }).catch(() => {});
    }

    history.push("/ingreso_hospitalario");
  }, [
    aiAssisted,
    minor,
    tutorName,
    resolveDocumentation,
    getAllFormValues,
    validateAllForms, 
    combineFormDataWithTimestamps, 
    validateCommentRequired, 
    saveCommentAutomatically, 
    saveCommentAutomaticallyUnam,
    caseRecord, 
    history,
    getFormCount,
    getRegisteredFormNames
  ]);

  sendRef.current = onClickUpdateCase;

  const commonFormProps = useMemo(() => ({
    icon: "fa-pencil-alt",
    data: caseRecord,
    disabledAll: false,
    enableMultiForm: true,
    bindSubmitForm: () => {}
  }), [caseRecord]);

  const buttonVisibility = useMemo(() => ({
    showControlDesk: !(queryParams.isScheduledSurgery === true && queryParams.isNewCase === false),
    showMedicInfo: queryParams.isScheduledSurgery === true && queryParams.isNewCase === false
  }), [queryParams]);

  return (
    <>
      <Alert
        variant="success"
        className="text-center"
        style={{ fontSize: "16px" }}
      >
        Ingreso Hospitalario
      </Alert>

      <p>{caseRecord.caseTicket}</p>

      {aiAssisted && (
        <>
          <IntakeHaloStyles />
          <IntakeSummary
            items={intakeItems}
            documents={documentChecklist}
            warnings={intakeNotices}
          />
          {minor && (
            <Form.Group className="mb-3">
              <Form.Label>Nombre del padre o tutor *</Form.Label>
              <Form.Control
                type="text"
                value={tutorName}
                onChange={(event) => setTutorName(event.target.value)}
                className={getHaloClass({
                  filled: tutorName.trim() !== "",
                  fromAI: tutorName === getTutorName(analyses) && tutorName !== "",
                })}
              />
            </Form.Group>
          )}
        </>
      )}
      {intakeModal}

      <BlockCaseContext.Provider value={[shouldBlockCase, setShouldBlockCase]}>
        <div ref={formsRef}>
        <Accordion defaultActiveKey="0">
          <HospitalData
            {...commonFormProps}
            eventKey="0"
            scheduledSurgery={queryParams.isScheduledSurgery}
            isNewCase={queryParams.isNewCase}
          />
          <InssuredData
            {...commonFormProps}
            eventKey="1"
          />
          <SufferingData
            {...commonFormProps}
            eventKey="2"
          />
          <FamilyData 
            {...commonFormProps}
            eventKey="3" 
          />
          <MedicAssigned
            {...commonFormProps}
            eventKey="4"
          />
          {!isEmpty(caseRecord) && (
            <Documentation 
              {...commonFormProps}
              eventKey="5" 
              slug={caseRecord.slug} 
            />
          )}
          <AuthLetters
            {...commonFormProps}
            showForm={true}
            eventKey="6"
            typeModule="Hospitalization"
            hideMedicField={true}
            hideComplement={true}
            hideSubstitution={true}
            {...props}
          />
          {!isEmpty(caseRecord) && (
            <Binnacle 
              {...commonFormProps}
              eventKey="7" 
              slug={caseRecord.slug} 
              eventStage="Ingreso hospitalario" 
            />
          )}
        </Accordion>
        </div>
      </BlockCaseContext.Provider>

      <Row>
        <Col hidden={!buttonVisibility.showControlDesk}>
          <Button 
            className="mt-5" 
            variant="primary" 
            onClick={() => onClickUpdateCase()}
          >
            Enviar a Mesa de Control
          </Button>
        </Col>

        <Col 
          md={3} 
          className="mt-5 mr-5" 
          hidden={!buttonVisibility.showMedicInfo}
        >
          <Button 
            variant="danger" 
            onClick={() => onClickUpdateCase(CASE_STATUS.MEDIC_INFORMATION)}
          >
            Enviar a Informacion Médica Hospital
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default compose(
  WithCommentValidation,
  WithMultiFormManager
)(Hospitalization);


